<script setup>
import { ref, computed } from 'vue';
import Button from './Button.vue';

const showPopup = ref(false);
const currentImageIndex = ref(0); // Tracks the currently selected image

const props = defineProps({
    images: Array,
    class: String,
});

// Computed property to get the current image
const currentImage = computed(() => props.images[currentImageIndex.value]);

// Function to show the modal with the selected image
function openPopup(index) {
    currentImageIndex.value = index;
    showPopup.value = true;
}

// Function to navigate to the previous image
function prevImage() {
    currentImageIndex.value = (currentImageIndex.value - 1 + props.images.length) % props.images.length;
}

// Function to navigate to the next image
function nextImage() {
    currentImageIndex.value = (currentImageIndex.value + 1) % props.images.length;
}

// Function to set the current image when clicking on an image from the preview list
function openImageFromPreview(index) {
    currentImageIndex.value = (currentImageIndex.value + index + 1) % props.images.length;
    showPopup.value = true; // Show the popup
}

// Function to close the modal
function closePopup() {
    showPopup.value = false;
}

// Computed property to calculate the images for the preview list
const previewImages = computed(() => {
    const start = currentImageIndex.value;
    const count = 4; // Default number of images
    const visibleCount = window.innerWidth >= 1024 ? 8 : window.innerWidth >= 768 ? 6 : count; // Responsive breakpoints
    return [...props.images.slice(start + 1), ...props.images.slice(0, start)]
        .slice(0, visibleCount); // Wrap around if needed
});

const showMore = ref(false);
</script>

<template>
    <div :class="['relative space-y-2 sm:space-y-3 md:space-y-3.5 lg:space-y-4', showMore ? 'pb-4 sm:pb-8 md:pb-12 lg:pb-16' : '']" v-auto-animate>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 md:gap-3.5 lg:gap-4">
            <div v-for="(image, index) in images.slice(1, 2)" :key="index"
                class="relative group rounded-lg overflow-hidden bg-white shadow border-[1px] hover:shadow-lg duration-150 aspect-[13/9] md:aspect-auto col-span-full md:col-span-2 md:row-span-2">
                <button @click="openPopup(index + 1)"
                    class="absolute top-0 left-0 right-0 bottom-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 h-full w-full flex items-center justify-center">
                    <font-awesome-icon :icon="['far', 'eye']" class="text-white h-8 w-8" />
                </button>
                <img :src="image.src" class="absolute top-0 left-0 right-0 bottom-0 object-cover h-full w-full" />
            </div>
            <div v-for="(image, index) in images.slice(2, 4)" :key="index"
                class="relative group rounded-lg overflow-hidden bg-white shadow border-[1px] hover:shadow-lg duration-150 aspect-[13/9]">
                <button @click="openPopup(index + 2)"
                    class="absolute top-0 left-0 right-0 bottom-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 h-full w-full flex items-center justify-center">
                    <font-awesome-icon :icon="['far', 'eye']" class="text-white h-8 w-8" />
                </button>
                <img :src="image.src" class="absolute top-0 left-0 right-0 bottom-0 object-cover h-full w-full" />
            </div>
        </div>

        <div v-if="showMore" class="col-span-full grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3 md:gap-3.5 lg:gap-4">
            <div v-for="(image, index) in images.slice(4)" :key="index"
                class="relative group rounded-lg overflow-hidden bg-white shadow border-[1px] hover:shadow-lg duration-150 aspect-[13/9]">
                <button @click="openPopup(index + 4)"
                    class="absolute top-0 left-0 right-0 bottom-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 h-full w-full flex items-center justify-center">
                    <font-awesome-icon :icon="['far', 'eye']" class="text-white h-8 w-8" />
                </button>
                <img :src="image.src" class="absolute top-0 left-0 right-0 bottom-0 object-cover h-full w-full" />
            </div>
        </div>

        <div :class="['absolute z-20 w-full sm:w-auto', showMore ? '-bottom-13' : '-bottom-13 sm:bottom-4 sm:left-4']">
            <Button @click="showMore = !showMore" class="primary-button">
            {{ showMore ? 'Toon minder' : `Toon meer (+${images.length - 5})` }}
            </Button>
        </div>

    </div>  
    <div v-if="showPopup" @click="showPopup = false"
        class="fixed top-0 left-0 right-0 bottom-0 bg-black/80 z-50 flex items-center justify-center w-full h-full">
        <div class="flex items-center justify-center w-full h-full">
            <img @click.stop :src="currentImage.src"
                class="rounded-2xl w-full sm:w-auto h-auto sm:h-full lg:pb-32 py-4 object-contain overflow-hidden" />
        </div>
        <button @click="closePopup" class="absolute top-4 right-4">
            <font-awesome-icon :icon="['far', 'xmark']" class="h-8 w-8 text-white hover:text-gray-300" />
        </button>
        <div @click.stop class="absolute bottom-1/2 left-4 transform -translate-y-1/2 flex gap-4">
            <!-- Previous Image -->
            <button @click="prevImage">
                <font-awesome-icon :icon="['far', 'chevron-left']" class="h-8 w-8 text-white hover:text-gray-300" />
            </button>
        </div>
        <div @click.stop class="absolute bottom-1/2 right-4 transform -translate-y-1/2 flex gap-4">
            <!-- Next Image -->
            <button @click="nextImage">
                <font-awesome-icon :icon="['far', 'chevron-right']" class="h-8 w-8 text-white hover:text-gray-300" />
            </button>
        </div>
        <div @click.stop class="absolute bottom-4 left-1/2 transform -translate-x-1/2 hidden lg:flex gap-4">
            <!-- Preview Thumbnails -->
            <div @click.stop v-for="(image, index) in previewImages" :key="index"
                class="relative group cursor-pointer rounded-xl overflow-hidden w-16 h-16 md:w-20 lg:w-24 md:h-20 lg:h-24">
                <button @click="openImageFromPreview(index)"
                    class="absolute top-0 left-0 right-0 bottom-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 h-full w-full flex items-center justify-center">
                    <font-awesome-icon :icon="['far', 'eye']" class="text-white h-6 w-6" />
                </button>
                <img :src="image.src" class="object-cover w-full h-full hover:scale-105" />
            </div>
        </div>
    </div>
</template>